import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';

import { useAuthContext } from '@/context/AuthProvider';
import { Task, TaskGroupView, groupTasks } from '@/models/Task';
import { completeTask, getTasks, uncompleteTask } from '@/services/api/tasks';

export interface UseTasksResult {
  tasks: Task[];
  taskGroups: TaskGroupView[];
  completedIds: Set<string>;
  totalCount: number;
  completedCount: number;
  allComplete: boolean;
  isLoadingTasks: boolean;
  toggleTaskComplete: (args: { taskId: string; isCompleted: boolean }) => void;
  isTogglingTask: boolean;
}

export const useTasks = (
  dealId: string | undefined,
  listingId?: string | undefined,
): UseTasksResult => {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const pendingTaskIds = useRef<Set<string>>(new Set());

  const queryKey = ['tasks', dealId, listingId];

  const {
    data: tasks,
    isLoading: isLoadingTasks,
    isFetching: isFetchingTasks,
  } = useQuery({
    queryKey,
    queryFn: () => getTasks(dealId as string, listingId),
    enabled: Boolean(dealId),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const { mutate, isPending: isTogglingTask } = useMutation({
    mutationFn: async ({ taskId, isCompleted }: { taskId: string; isCompleted: boolean }) => {
      if (isCompleted) {
        return uncompleteTask(taskId);
      }
      return completeTask(taskId);
    },
    onMutate: async ({ taskId, isCompleted }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousTasks = queryClient.getQueryData<Task[]>(queryKey);

      queryClient.setQueryData<Task[]>(queryKey, (old) =>
        old?.map((task) =>
          task.id === taskId
            ? {
                ...task,
                completedAt: isCompleted ? undefined : Math.floor(Date.now() / 1000),
                completedBy: isCompleted ? undefined : user?.id,
              }
            : task,
        ),
      );

      return { previousTasks, taskId };
    },
    onSuccess: (updatedTask, _variables, context) => {
      queryClient.setQueryData<Task[]>(queryKey, (old) =>
        old?.map((task) => (task.id === updatedTask.id ? updatedTask : task)),
      );
      if (context?.taskId) {
        pendingTaskIds.current.delete(context.taskId);
      }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(queryKey, context.previousTasks);
      }
      if (context?.taskId) {
        pendingTaskIds.current.delete(context.taskId);
      }
    },
  });

  const toggleTaskComplete = useCallback(
    ({ taskId, isCompleted }: { taskId: string; isCompleted: boolean }) => {
      if (pendingTaskIds.current.has(taskId)) return;
      pendingTaskIds.current.add(taskId);
      mutate({ taskId, isCompleted });
    },
    [mutate],
  );

  const taskList = useMemo<Task[]>(() => tasks ?? [], [tasks]);
  const taskGroups = useMemo(() => groupTasks(taskList), [taskList]);
  const completedIds = useMemo(
    () => new Set(taskList.filter((t) => t.completedAt).map((t) => t.id)),
    [taskList],
  );

  return {
    tasks: taskList,
    taskGroups,
    completedIds,
    totalCount: taskList.length,
    completedCount: completedIds.size,
    allComplete: taskList.length > 0 && completedIds.size === taskList.length,
    isLoadingTasks: isLoadingTasks || isFetchingTasks,
    toggleTaskComplete,
    isTogglingTask,
  };
};
